'use client';

import * as React from 'react';
import { startTransition } from 'react';
import { Fire, Sparkle, Pepper, Plus } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { Badge3D, CornerRibbon } from '@/components/ui';
import { QuantitySelector } from '@/components/ui/inputs/quantity-selector';
import { formatPrice } from '@/lib/utils';
import { ThemedImage } from '@/components/ui/themed-image';

// Types
export interface Product {
  id: number;
  name: string;
  price: number;
  image_url: string;
  description?: string;
  spiciness?: 'none' | 'spicy';
  isNew?: boolean;
  isPopular?: boolean;
  isRiceless?: boolean;
  isScorched?: boolean;
  isMini?: boolean;
  isAvailable?: boolean;
}

export interface ProductCardProps {
  product: Product;
  quantity?: number;
  onAddToCart?: (product: Product) => void;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onOpenPopup?: (product: Product) => void;
  className?: string;
  /** Приоритетная загрузка изображения (для первых карточек) */
  priority?: boolean;
}

// Product Card Component
const ProductCard = React.memo<ProductCardProps>(
  ({
    product,
    quantity = 0,
    onAddToCart,
    onQuantityChange,
    onOpenPopup,
    className,
    priority = false,
  }) => {
    const {
      id,
      name,
      price,
      image_url,
      spiciness = 'none',
      isNew = false,
      isPopular = false,
      isRiceless = false,
      isScorched = false,
      isMini = false,
      isAvailable = true,
    } = product;

    const isInCart = quantity > 0;

    // Отложенное состояние для обратной анимации
    // showSelector остаётся true на 200ms после удаления из корзины,
    // чтобы ширина успела сжаться до смены контента
    const [showSelector, setShowSelector] = React.useState(isInCart);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

    React.useEffect(() => {
      if (isInCart) {
        // Товар добавлен — сразу показываем селектор
        clearTimeout(timeoutRef.current);
        setShowSelector(true);
      } else if (showSelector) {
        // Товар убран — ждём окончания анимации сжатия
        timeoutRef.current = setTimeout(() => setShowSelector(false), 200);
      }
      return () => clearTimeout(timeoutRef.current);
    }, [isInCart]); // eslint-disable-line react-hooks/exhaustive-deps

    // Используем useCallback для стабильных обработчиков
    const handleAddToCart = React.useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isAvailable) return;
      startTransition(() => {
        onAddToCart?.(product);
      });
    }, [isAvailable, onAddToCart, product]);

    const handleQuantityChange = React.useCallback((newQuantity: number) => {
      onQuantityChange?.(id, newQuantity);
    }, [id, onQuantityChange]);

    // Используем startTransition для неблокирующего обновления UI
    const handleCardClick = React.useCallback((e: React.MouseEvent) => {
      // Предотвращаем всплытие только для клика по карточке (не по кнопкам)
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      startTransition(() => {
        onOpenPopup?.(product);
      });
    }, [onOpenPopup, product]);

    const handleStopPropagation = React.useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <div
        className={cn(
          'group relative',
          'bg-[var(--surface-primary)]',
          'border border-[var(--border-default)]',
          'rounded-card overflow-hidden',
          'cursor-pointer',
          'md:will-change-transform',
          !isAvailable && 'opacity-60',
          'md:hover:border-[var(--primary)]/30',
          // Карточка товара — витрина, объём ей оставлен (ответ 35 = 2).
          //
          // Здесь стояла пара «тень + фиолетовый цвет тени через переменную».
          // Проверено компиляцией: Tailwind 4 разбирал переменную как ЗНАЧЕНИЕ
          // тени, а не как её цвет, поэтому фиолетового ореола не получалось
          // никогда; вдобавок в собранном CSS этот класс шёл РАНЬШЕ соседнего
          // и всё равно проигрывал ему. То есть рисовалась обычная чёрная
          // тень, а фиолетовая половина была мёртвым кодом — она удалена.
          // (Имена классов намеренно не вписаны: сканер Tailwind читает
          // комментарии как обычный текст и вернул бы утилиту в сборку.)
          'md:hover:-translate-y-0.5 md:hover:shadow-float',
          'md:transition-[transform,box-shadow,border-color] md:duration-150',
          className
        )}
        onClick={handleCardClick}
      >
        {/* Corner Ribbon — основной бейдж слева */}
        {isPopular && <CornerRibbon variant="popular" />}
        {!isPopular && isNew && <CornerRibbon variant="new" />}
        {!isPopular && !isNew && isRiceless && <CornerRibbon variant="riceless" />}
        {!isPopular && !isNew && !isRiceless && isScorched && <CornerRibbon variant="scorched" />}
        {!isPopular && !isNew && !isRiceless && !isScorched && isMini && <CornerRibbon variant="mini" />}

        {/* Острое — всегда справа */}
        {spiciness === 'spicy' && (
          <CornerRibbon variant="spicy" position="right" />
        )}

        {/* Image Container */}
        <div className="relative aspect-[4/3.5] md:aspect-[5/4] bg-[var(--surface-secondary)] overflow-hidden [.single-col-grid_&]:aspect-[4/2.2] [.single-col-grid_&]:sm:aspect-[4/3.5]">
          <ThemedImage
            src={image_url}
            alt={name}
            fill
            optimized
            priority={priority}
            className="object-cover scale-[1.25]"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 280px"
          />

          {/* Cart Overlay - показывается только на мобиле когда товар в корзине */}
          {isInCart && (
            <div
              className={cn(
                'absolute inset-0 md:hidden',
                'bg-[var(--primary)]/70',
                'flex items-end justify-center pb-3'
              )}
            >
              <span className="text-white text-sm font-semibold">
                {formatPrice(price * quantity)} × {quantity}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-2 md:p-2.5 flex flex-col gap-1.5 md:gap-2">
          {/* Title */}
          <h3 className="font-semibold text-xs md:text-sm text-[var(--text-primary)] line-clamp-1">
            {name}
          </h3>

          {/* Price / Add to Cart */}
          <div className="mt-auto" onClick={handleStopPropagation}>
            {/* Mobile Layout */}
            <div className="md:hidden flex items-center justify-end gap-3 h-8">
              {!isInCart && (
                <div className="flex-1 min-w-0">
                  <span className="text-base font-bold text-[var(--primary)]">
                    {formatPrice(price)}
                  </span>
                </div>
              )}

              {/* Animated cart control */}
              <div
                className={cn(
                  'flex items-center justify-end shrink-0',
                  'h-8 rounded-pill overflow-hidden',
                  'transition-[width] duration-200 ease-out',
                  isInCart ? 'w-[96px]' : 'w-8'
                )}
              >
                {showSelector ? (
                  <QuantitySelector
                    value={quantity || 1}
                    onChange={handleQuantityChange}
                    size="sm"
                  />
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={!isAvailable}
                    className={cn(
                      'flex items-center justify-center',
                      'w-8 h-8 shrink-0 rounded-pill',
                      isAvailable
                        ? 'bg-[var(--primary-subtle)] text-[var(--primary)] active:bg-[var(--primary-muted)]'
                        : 'bg-[var(--surface-disabled)] text-[var(--text-disabled)] cursor-not-allowed'
                    )}
                  >
                    <Plus className="h-3.5 w-3.5" weight="bold" />
                  </button>
                )}
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex items-center justify-between gap-3">
              <div className="flex-1">
                <span className="text-base font-bold text-[var(--primary)]">
                  {formatPrice(price)}
                </span>
              </div>

              {/* Animated cart control */}
              <div
                className={cn(
                  'flex items-center justify-end shrink-0',
                  'h-10 rounded-pill overflow-hidden',
                  'transition-[width] duration-200 ease-out',
                  isInCart ? 'w-[120px]' : 'w-10'
                )}
              >
                {showSelector ? (
                  <QuantitySelector
                    value={quantity || 1}
                    onChange={handleQuantityChange}
                    size="default"
                  />
                ) : (
                  <button
                    onClick={handleAddToCart}
                    disabled={!isAvailable}
                    className={cn(
                      'flex items-center justify-center',
                      'w-10 h-10 shrink-0 rounded-pill',
                      'transition-[background-color] duration-100',
                      isAvailable
                        ? 'bg-[var(--primary-subtle)] text-[var(--primary)] hover:bg-[var(--primary-muted)] active:bg-[var(--primary-light)]'
                        : 'bg-[var(--surface-disabled)] text-[var(--text-disabled)] cursor-not-allowed'
                    )}
                  >
                    <Plus className="h-5 w-5" weight="bold" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);
ProductCard.displayName = 'ProductCard';

export { ProductCard };
